import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletDrawModule } from '@bluehalo/ngx-leaflet-draw';
import { isDefined, isNil } from '@trimble-gcs/common';
import bbox from '@turf/bbox';
import { Feature, FeatureCollection } from 'geojson';
import {
  Draw,
  DrawEvents,
  DrawMap,
  geoJSON,
  Icon,
  icon,
  IconOptions,
  LatLng,
  latLng,
  LatLngBounds,
  Layer,
  LeafletEvent,
  Map,
  MapOptions,
  marker,
  PathOptions,
  Point,
  PointExpression,
  Polygon,
  Rectangle,
} from 'leaflet';

import { BaseLayer } from '../base-layer/base-layer.models';
import { FeatureLayer, NORMAL_STYLE, SELECTED_STYLE } from '../feature-layer/feature-layer.models';
import { MapBounds, MapTool } from '../map.models';
import {
  calculateCentroid,
  convertBBoxToLeafletBounds,
  convertLeafletBoundsToBBox,
  findFeaturesIntersectingPolygon,
  getBBoxWidthAndHeight,
} from '../map.util';
import { MarkerLayer } from './marker-layer';
import { TileLayerWithHeaders } from './tile-layer-with-headers';

const DOUBLE_CLICK_TIMEOUT = 300;
const ZOOM_THRESHOLD = 14;
const SHOW_POLYGON_THRESHOLD = 0.2;
const MAP_LAT_MIN = -90;
const MAP_LAT_MAX = 90;
const MAP_MAX_ZOOM = 20;

const defaultOptions = {
  attributionControl: false,
  boxZoom: false,
  zoom: 0,
  minZoom: 0,
  maxZoom: MAP_MAX_ZOOM,
  zoomControl: false,
  zoomSnap: 0.001,
  center: latLng(0, 0),
  maxBounds: new LatLngBounds(
    new LatLng(MAP_LAT_MIN, Number.NEGATIVE_INFINITY),
    new LatLng(MAP_LAT_MAX, Number.POSITIVE_INFINITY),
  ),
  maxBoundsViscosity: 1,
  worldCopyJump: true,
} satisfies MapOptions;

const drawShapeOptions: PathOptions = {
  weight: 3,
  opacity: 1,
  color: '#FBAD26',
  fillColor: '#FBAD26',
};

const drawMarker = Icon.extend({
  options: {
    iconAnchor: new Point(5, 5),
    iconUrl: 'assets/leaflet/draw-marker.png',
  },
});

export interface FeatureClickedEvent {
  feature: Feature;
  hasModifierKey: boolean;
}

export interface ClipRegionDrawnEvent {
  polygon: GeoJSON.Polygon;
  boundsWest: number;
  boundsEast: number;
}

@Component({
  selector: 'sd-map-viewer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LeafletModule, LeafletDrawModule],
  templateUrl: './map-viewer.component.html',
  styleUrls: ['./map-viewer.component.scss'],
})
export class MapViewerComponent implements AfterViewInit, OnDestroy {
  mapElement = viewChild.required<ElementRef<HTMLDivElement>>('map');

  baseLayer = input.required<Layer | null, BaseLayer | null>({
    transform: (inputLayer: BaseLayer | null) => {
      if (isNil(inputLayer)) return null;

      return new TileLayerWithHeaders(inputLayer.templateUrl, inputLayer.headers, {
        maxZoom: MAP_MAX_ZOOM,
      });
    },
  });

  featureLayer = input.required<FeatureLayer | null>();
  bounds = input.required<MapBounds>();
  activeMapTool = input.required<MapTool>();
  clipRegion = input<GeoJSON.Polygon | undefined>();

  featureClicked = output<FeatureClickedEvent>();
  featureDblClicked = output<FeatureClickedEvent>();
  featuresSelected = output<Feature[]>();
  clipRegionDrawn = output<ClipRegionDrawnEvent>();
  clipRegionCleared = output<void>();
  mapToolSelected = output<MapTool>();
  boundsChanged = output<MapBounds>();

  scanLayer = signal<Layer>(geoJSON());

  options: MapOptions = defaultOptions;

  private leafletMap?: Map;
  private zoom = defaultOptions.zoom;
  private polygonLayer: Layer = geoJSON();
  private markerLayer: Layer = geoJSON();
  private resizeObserver!: ResizeObserver;
  private drawRectangle?: Draw.Rectangle;
  private drawPolygon?: Draw.Polygon;
  private drawClipRectangle?: Draw.Rectangle;
  private drawClipPolygon?: Draw.Polygon;
  private clipRegionLayer: Layer = geoJSON();
  private currentBounds!: LatLngBounds;

  private get drawMap() {
    return this.leafletMap as DrawMap;
  }

  constructor() {
    this.createBoundsEffect();
    this.createFeatureLayerEffect();
    this.createMapToolEffect();
    this.createClipRegionEffect();
    this.createClipDrawReenableEffect();
  }

  ngAfterViewInit() {
    this.registerResizeObserver();
  }

  ngOnDestroy(): void {
    const bbox = convertLeafletBoundsToBBox(this.currentBounds);
    this.boundsChanged.emit({ bbox });

    this.resizeObserver.disconnect();
  }

  zoomed(event: LeafletEvent) {
    if (!this.isMapVisible()) return;

    this.setWorldCopyInBounds();
    this.updateDataLayer(this.featureLayer());

    this.zoom = event.target.getZoom();
  }

  zoomIn() {
    this.leafletMap?.zoomIn();
  }

  zoomOut() {
    this.leafletMap?.zoomOut();
  }

  moved() {
    if (isNil(this.leafletMap) || !this.isMapVisible()) return;

    this.setWorldCopyInBounds();
    this.updateDataLayer(this.featureLayer());

    this.currentBounds = this.leafletMap.getBounds();
  }

  setMapInstance(map: Map) {
    this.leafletMap = map;

    this.addMapRightClickHandler(this.leafletMap);
    this.addMapDrawHandler(this.leafletMap);
    this.addPanHandler(this.leafletMap);
  }

  private createBoundsEffect() {
    effect(() => {
      /**
       * Invalidating map size & set min zoom.
       * The effect runs early, caching the map size before material has finished animation.
       * See comment for the same issue with fitToMapBounds in resizeObserver.
       */
      this.resetMapSize();
      this.fitMapToBounds(this.bounds());
    });
  }

  private createFeatureLayerEffect() {
    effect(() => {
      const interactive = this.activeMapTool() === MapTool.Default;
      const featureLayer = this.featureLayer();

      this.createDataLayers(featureLayer, interactive);
      this.updateDataLayer(featureLayer);
    });
  }

  private createMapToolEffect() {
    effect(() => {
      const activeMapTool = this.activeMapTool();

      this.drawRectangle?.disable();
      this.drawPolygon?.disable();
      this.drawClipRectangle?.disable();
      this.drawClipPolygon?.disable();

      switch (activeMapTool) {
        case MapTool.RectangleSelect:
          this.drawRectangleSelect();
          break;

        case MapTool.PolygonSelect:
          this.drawPolygonSelect();
          break;

        case MapTool.ClipRectangle:
          this.drawClipRectangleSelect();
          break;

        case MapTool.ClipPolygon:
          this.drawClipPolygonSelect();
          break;
      }
    });
  }

  private createClipRegionEffect() {
    effect(() => {
      const clipRegion = this.clipRegion();
      const map = this.leafletMap;
      if (isNil(map)) return;

      map.removeLayer(this.clipRegionLayer);

      if (isNil(clipRegion)) return;

      this.clipRegionLayer = geoJSON(clipRegion, { style: drawShapeOptions });
      this.clipRegionLayer.addTo(map);
    });
  }

  private createClipDrawReenableEffect() {
    // Re-arm the clip tool when the region is cleared externally (e.g. Clip & Save panel closed).
    effect(() => {
      const clipRegion = this.clipRegion();
      const activeMapTool = this.activeMapTool();

      if (isDefined(clipRegion)) return;

      this.enableClipDrawTool(activeMapTool);
    });
  }

  private enableClipDrawTool(activeMapTool: MapTool) {
    switch (activeMapTool) {
      case MapTool.ClipRectangle:
        this.drawClipRectangle?.enable();
        break;
      case MapTool.ClipPolygon:
        this.drawClipPolygon?.enable();
        break;
    }
  }

  private drawRectangleSelect() {
    this.drawRectangle =
      this.drawRectangle ??
      new Draw.Rectangle(this.drawMap, {
        shapeOptions: drawShapeOptions,
        repeatMode: true,
      });
    this.drawRectangle.enable();
  }

  private drawPolygonSelect() {
    this.drawPolygon =
      this.drawPolygon ??
      new Draw.Polygon(this.drawMap, {
        shapeOptions: drawShapeOptions,
        repeatMode: true,
        icon: new drawMarker(),
      });
    this.drawPolygon.enable();
  }

  private drawClipRectangleSelect() {
    this.drawClipRectangle =
      this.drawClipRectangle ??
      new Draw.Rectangle(this.drawMap, {
        shapeOptions: drawShapeOptions,
        repeatMode: true,
      });
    this.drawClipRectangle.enable();
  }

  private drawClipPolygonSelect() {
    this.drawClipPolygon =
      this.drawClipPolygon ??
      new Draw.Polygon(this.drawMap, {
        shapeOptions: drawShapeOptions,
        repeatMode: true,
        icon: new drawMarker(),
      });
    this.drawClipPolygon.enable();
  }

  private addMapRightClickHandler(leafletMap: Map) {
    leafletMap.on('mousedown', (event) => {
      if (event.originalEvent.button === 0) return;

      this.mapToolSelected.emit(MapTool.Default);
    });

    leafletMap.on('contextmenu', (event) => {
      event.originalEvent.stopPropagation();
    });
  }

  private addMapDrawHandler(leafletMap: Map) {
    // This is a hack due to a bug in leaflet.draw
    // https://github.com/Leaflet/Leaflet.draw/issues/1005
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).type = undefined;

    leafletMap.on('draw:canceled', () => {
      const activeMapTool = this.activeMapTool();
      const isClipTool =
        activeMapTool === MapTool.ClipRectangle || activeMapTool === MapTool.ClipPolygon;

      // Esc clears the drawn region but keeps the clip tool active to redraw.
      if (isClipTool && isDefined(this.clipRegion())) {
        this.clipRegionCleared.emit();

        // Re-arm after Leaflet's synchronous disable() that follows draw:canceled.
        queueMicrotask(() => this.enableClipDrawTool(activeMapTool));
        return;
      }

      this.mapToolSelected.emit(MapTool.Default);
    });

    leafletMap.on(Draw.Event.CREATED, (event) => {
      const activeMapTool = this.activeMapTool();
      const { layer } = event as DrawEvents.Created;
      const drawShape = layer as Polygon | Rectangle;
      const { geometry: drawPolygon } = drawShape.toGeoJSON() as Feature<GeoJSON.Polygon, unknown>;
      const bounds = drawShape.getBounds();

      if (activeMapTool === MapTool.ClipRectangle || activeMapTool === MapTool.ClipPolygon) {
        leafletMap.removeLayer(layer);
        this.clipRegionDrawn.emit({
          polygon: drawPolygon,
          boundsWest: bounds.getWest(),
          boundsEast: bounds.getEast(),
        });
        return;
      }

      const features = this.featureLayer()?.featureCollection?.features;
      if (isNil(features) || features.length === 0) return;

      const intersectingFeatures = findFeaturesIntersectingPolygon(
        drawPolygon,
        bounds.getWest(),
        bounds.getEast(),
        features,
      );

      this.featuresSelected.emit(intersectingFeatures);
    });
  }

  /**
   * While panning with inertia the map should immediatly stop on mouse down,
   * leaflet does not do this out the box, this method fixes that.
   */
  private addPanHandler(leafletMap: Map) {
    let panning = false;

    leafletMap.on('movestart', () => (panning = true));
    leafletMap.on('moveend', () => (panning = false));
    leafletMap.on('mousedown', () => {
      if (panning) leafletMap.stop();
    });
  }

  private createDataLayers(featureLayer: FeatureLayer | null, interactive: boolean) {
    this.polygonLayer = isDefined(featureLayer)
      ? this.createPolygonLayer(featureLayer, interactive)
      : geoJSON();
    this.markerLayer = isDefined(featureLayer)
      ? this.createMarkerLayer(featureLayer, interactive)
      : new MarkerLayer();
  }

  private createPolygonLayer(featureLayer: FeatureLayer, interactive: boolean): Layer {
    const featureClicked = this.featureClicked;

    // Reverse features so 1st item is added last to the layer and top of the z-index;
    const featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features: featureLayer.featureCollection.features.toReversed(),
    };

    return geoJSON(featureCollection, {
      interactive,
      style: function (feature) {
        if (feature?.properties.selected) {
          return SELECTED_STYLE;
        } else {
          return NORMAL_STYLE;
        }
      },
      filter: function (feature) {
        return feature?.properties.visible;
      },
      onEachFeature: function onEachFeature(feature, layer) {
        layer.on({
          click: ($event) => {
            const hasModifier = $event.originalEvent.ctrlKey || $event.originalEvent.shiftKey;
            featureClicked.emit({ feature, hasModifierKey: hasModifier });
          },
        });
      },
    });
  }

  private createMarkerLayer(featureLayer: FeatureLayer, interactive: boolean): Layer {
    const markerLayer = new MarkerLayer();
    const markers = this.createMarkers(featureLayer, interactive);

    markers?.forEach((marker) => markerLayer.addMarker(marker));

    return markerLayer;
  }

  private createMarkers(featureLayer: FeatureLayer, interactive: boolean) {
    const featureClicked = this.featureClicked;
    const featureDblClicked = this.featureDblClicked;

    const markers =
      featureLayer.featureCollection?.features
        .toReversed()
        .filter((feature) => feature?.properties?.['visible'])
        .map((feature, index) => {
          const centroid = calculateCentroid(feature);
          const [lng, lat] = centroid.geometry.coordinates;

          const iconName = feature?.properties?.['selected'] ? 'selected' : 'marker';

          const iconOptions: IconOptions = {
            iconSize: [25, 41],
            iconAnchor: [13, 41],
            shadowUrl: 'assets/leaflet/marker-shadow.png',
            iconUrl: `assets/leaflet/${iconName}.png`,
            iconRetinaUrl: `assets/leaflet/${iconName}-2x.png`,
          };

          let clickHandled = false;

          return marker(latLng(lat, lng), {
            interactive,
            icon: icon(iconOptions),
            // Exaggerating index with 10, because leaflet messes up it's own internal index every now and then.
            zIndexOffset: index * 10,
          })
            .on('click', ($event) => {
              clickHandled = false;
              window.setTimeout(() => {
                if (!clickHandled) {
                  const hasModifier = $event.originalEvent.ctrlKey || $event.originalEvent.shiftKey;
                  featureClicked.emit({ feature, hasModifierKey: hasModifier });
                }
              }, DOUBLE_CLICK_TIMEOUT);
            })
            .on('dblclick', ($event) => {
              clickHandled = true;

              const hasModifier = $event.originalEvent.ctrlKey || $event.originalEvent.shiftKey;
              featureDblClicked.emit({ feature, hasModifierKey: hasModifier });
            });
        }) || [];

    return markers;
  }

  private updateDataLayer(featureLayer: FeatureLayer | null) {
    const showPolygonLayer =
      this.zoom >= ZOOM_THRESHOLD ||
      (isDefined(featureLayer) && this.showFeaturePolygon(featureLayer));

    const showLayer = showPolygonLayer ? this.polygonLayer : this.markerLayer;
    this.scanLayer.set(showLayer);
  }

  private showFeaturePolygon(featureLayer: FeatureLayer) {
    if (isNil(featureLayer.featureCollection)) return false;

    const mapBounds = this.leafletMap?.getBounds();
    if (isNil(mapBounds)) return false;

    const mapBBox = convertLeafletBoundsToBBox(mapBounds);

    return featureLayer.featureCollection.features.some((feature) => {
      const featureBounds = geoJSON(feature).getBounds();
      if (!mapBounds.intersects(featureBounds)) return false;

      const mapSize = getBBoxWidthAndHeight(mapBBox);
      const featureSize = getBBoxWidthAndHeight(bbox(feature));

      const featureWidthRatio = featureSize.width / mapSize.width;
      const featureHeightRatio = featureSize.height / mapSize.height;

      return (
        featureWidthRatio >= SHOW_POLYGON_THRESHOLD || featureHeightRatio >= SHOW_POLYGON_THRESHOLD
      );
    });
  }

  private registerResizeObserver() {
    let firstResizeEvent = true;

    this.resizeObserver = new ResizeObserver(() => {
      this.resetMapSize();

      if (firstResizeEvent) {
        firstResizeEvent = false;

        /**
         * Ideally we would fit bounds on either setMapInstance or afterViewInit.
         *
         * The problem is that when you switch tabs, material hasn't finished setting
         * the final size of the container until this resize event fires.
         *
         * The result is that leaflet has cached the incorrect container size, and
         * wont set the bounds correctly.
         * (also note leaflets internal resizeObserver should be invalidating size,
         * but it is not working as at v1.9.4)
         *
         * Therefore we can only reliably set the bounds after the resize event and
         * after calling leaflet invalidateSize.
         *
         * So here we are checking if this is component init (via firstResize) and
         * then fitting the map to the input bounds supplied.
         */
        this.fitMapToBounds(this.bounds());
      }
    });

    this.resizeObserver.observe(this.mapElement().nativeElement);
  }

  /**
   * worldCopyJump = true is suppose to take care of this,
   * but it only works when you pan with mouse down or the
   * keyboard.
   * It does not work when panning with inertia or zoom.
   * This method does what worldCopyJump is suppose to do.
   */
  private setWorldCopyInBounds() {
    const map = this.leafletMap;
    if (isNil(map)) return;

    const center = map.getCenter();
    const newCenter = map.wrapLatLng(center);

    if (center.equals(newCenter)) return;

    map.panTo(newCenter, { animate: false, noMoveStart: true });
  }

  private setMinZoom() {
    const map = this.leafletMap;
    if (isNil(map)) return;

    const minBounds = new LatLngBounds(new LatLng(MAP_LAT_MIN, 0), new LatLng(MAP_LAT_MAX, 0));

    /**
     * getBoundsZoom takes the current min zoom into account. We have to
     * first set min zoom back to zero before calling getBoundsZoom.
     *
     * leaflet.setMinZoom updates the map options, but it also calls setView
     * which produces incorrect results when immediatly calling fitBounds thereafter
     * (but only for small bounds with for example a zoom level 18).
     *
     * setMinZoom does not have a config options parameter, so you can't set
     * animation=false which should fix the problem.
     *
     * Calling map.stop() before fitBounds should have fixed the issue but
     * it has no effect and fitBounds still produces the wrong results.
     *
     * In Summary: the code below does what setMinZoom does, but without the
     * animation.
     */

    const minZoom = map.getBoundsZoom(minBounds);
    if (minZoom === MAP_MAX_ZOOM) return; //this will happen if mapElement DOMRect is 0,0,0,0

    map.options.minZoom = 0;
    map.options.minZoom = minZoom;

    if (map.getZoom() < map.options.minZoom) {
      map.setZoom(map.options.minZoom, { animate: false });
    }
  }

  private fitMapToBounds(bounds: MapBounds) {
    if (isNil(this.leafletMap)) return;

    const leafletBounds = convertBBoxToLeafletBounds(bounds.bbox);
    const paddingValue = bounds.padding ?? 0;
    const padding: PointExpression = [paddingValue, paddingValue];

    this.leafletMap.fitBounds(leafletBounds, { padding });
  }

  private isMapVisible() {
    const map = this.mapElement().nativeElement;
    return map.offsetHeight > 0 && map.offsetWidth > 0;
  }

  private resetMapSize() {
    if (isNil(this.leafletMap)) return;

    this.leafletMap.invalidateSize({ pan: false, animate: false, debounceMoveend: true });
    this.setMinZoom();
  }
}
